import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance, { axiosCacheInstance } from '../../../axiosSetup';

const safeDate = (value: any): Date | null => {
    if (!value) return null;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
};

export const submitInvoiceRequest = createAsyncThunk(
    'invoiceRequest/submitInvoiceRequest',
    async (finalPayload: any, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(
                `/api/edo-rest-v2/v2/invoiceRequest/submitInvoiceRequestInstruction`,
                finalPayload
            );

            const data = response.data ?? {};
            if (data.status === 'FAILED') {
                return rejectWithValue(data);
            }
            if (data.status && data.status !== 'SUCCESS') {
                return rejectWithValue(data);
            }

            return data;
        } catch (error: any) {
            const errData = error?.response?.data;
            return rejectWithValue(
                errData?.status === 'FAILED' || errData?.responseMsg
                    ? errData
                    : {
                          status: 'FAILED',
                          responseMsg: 'Error while submitting Invoice Request',
                      }
            );
        }
    }
);

const mergeDocList = (
    payloadDocList: any[],
    dropdownDocList: any[],
    attachmentData: any[]
): { updatedDocList: any[] } => {
    if (!Array.isArray(payloadDocList)) payloadDocList = [];
    if (!Array.isArray(dropdownDocList) || dropdownDocList.length === 0) {
        return { updatedDocList: payloadDocList };
    }
    if (!Array.isArray(attachmentData)) attachmentData = [];

    const dropdownDocId = dropdownDocList.map((i: any) => i.id);
    const dropdownDocs = attachmentData
        .filter((i: any) => dropdownDocId.includes(i.docId))
        .map((e: any) => ({
            rowNumber: crypto.randomUUID(),
            id: e?.docId,
            name: dropdownDocList.find((i: any) => i?.id === e?.docId)?.name ?? '',
            m: true,
            isDropDownDoc: true,
        }));

    return { updatedDocList: [...payloadDocList, ...dropdownDocs] };
};

const docConfigAndAttData = (payloadDocList: any[]) => {
    if (!Array.isArray(payloadDocList) || payloadDocList.length === 0) {
        return { dropdownDocList: [], docList: [], attDataList: [] };
    }

    const updatedDocList =
        payloadDocList
            ?.filter((i: any) => i?.isMandatory != 'H' && i?.isMandatory != 'E')
            ?.map?.((i: any) => ({
                rowNumber: crypto.randomUUID(),
                id: i?.docId,
                name: i?.docName,
                m: i?.isMandatory == 'Y',
            })) ?? [];

    const dropdownOptions =
        payloadDocList
            ?.filter((i: any) => i?.isMandatory == 'H')
            ?.map?.((i: any) => ({
                id: i?.docId,
                name: i?.docName,
                m: true,
                isDropDownDoc: true,
            })) ?? [];

    const attDataList = (updatedDocList ?? [])?.map((x: any) => ({
        rowNumber: x.rowNumber,
        attachmentId: null,
        docId: x.id,
        fileName: '',
        filePath: null,
        m: x.m,
        ...(x.hasOwnProperty('isDropDownDoc') ? { isDropDownDoc: x.isDropDownDoc } : {}),
    }));

    return {
        dropdownDocList: dropdownOptions ?? [],
        docList: updatedDocList ?? [],
        attDataList,
    };
};

export const getInvoiceRequestDetails = createAsyncThunk(
    'invoiceRequest/getInvoiceRequestDetails',
    async (payload: { invoiceRequestId: any; country: string; linerId: any }, { rejectWithValue }) => {
        try {
            const docConfigResponse = await axiosCacheInstance.get(
                `/api/edo-rest-v2/v2/common/attachments?countryCode=${payload.country}&linerId=${payload.linerId}&instructionType=822`,
                { cache: { ttl: 10 * 60 * 1000 } }
            );

            const { dropdownDocList, docList } = docConfigAndAttData(docConfigResponse?.data);

            const response = await axiosInstance.get(
                `/api/edo-rest-v2/v2/invoiceRequest/getInvoiceRequestById?invoiceRequestId=${payload?.invoiceRequestId}&linerId=${payload.linerId}&countryCode=${payload.country}`
            );

            const data = response?.data ?? {};
            // const data = raw?.data ?? raw;

            const documents = (data?.invoiceRequestInstructionAtts ?? []).map((f: any) => ({
                ...f,
                docid: f?.docid ?? f?.docId,
            }));
            const { updatedDocList } = mergeDocList(docList, dropdownDocList, documents);

            const visitedAttId: any[] = [];
            const attList =
                updatedDocList?.map((x: any) => {
                    const value = documents?.find(
                        (f: any) =>
                            f?.docid === x?.id &&
                            !visitedAttId.includes(f?.invoiceRequestInstructionAttId)
                    );

                    if (value?.invoiceRequestInstructionAttId) {
                        visitedAttId.push(value?.invoiceRequestInstructionAttId);
                    }

                    return {
                        rowNumber: x?.rowNumber,
                        attachmentId: value?.invoiceRequestInstructionAttId ?? null,
                        docId: x?.id,
                        filePath: value?.filePath || null,
                        fileName: value?.fileName || '',
                        m: x?.m,
                        isDropDownDoc: x?.isDropDownDoc,
                    };
                }) ?? [];

            const   savedContainerList = data?.containerList ?? [];
            const blId = data?.blId ?? null;
            let containerTableData: any[] = [];

            // OBL API = full container list (table structure with empty fields)
            // GET API containerList = saved values mapped by containerNo:
            // invoiceRequestContainerId, invoiceRequestInstructionId, pickupDate, pickupLocation, currency
            if (blId) {
                try {
                    const oblResponse = await axiosInstance.get(
                        `/api/edo-rest-v2/v2/coreor/blDoc/${blId}?linerId=${payload.linerId}`
                    );

                    oblResponse?.data?.blJourneyList?.sort(
                        (a: any, b: any) => a.blJourneyId - b.blJourneyId
                    );

                    containerTableData = (oblResponse?.data?.containerList ?? []).map(
                        (i: any) => ({
                            containerNo: i.containerNo,
                            invoiceRequestContainerId: null as number | null,
                            invoiceRequestInstructionId: null as number | null,
                            deliveryZone: String(data?.deliveryZoneId ?? ''),
                            deliveryZoneName: data?.deliveryZoneName ?? '',
                            subLocation: String(data?.subLocationId ?? ''),
                            subLocationName: data?.subLocationName ?? '',
                            modeOfTransport: String(data?.modeOfTransport ?? ''),
                            typeOfTrailer: String(data?.typeOfTrailer ?? ''),
                            isHazardous: data?.isHazardous ?? false,
                            needExtensionFor: '0',
                            plannedPickupDate: null as Date | null,
                            currency: 'XOF',
                        })
                    );

                    savedContainerList.forEach((item: any) => {
                        const row = containerTableData.find(
                            (x) =>
                                String(x.containerNo).trim() ===
                                String(item.containerNo ?? '').trim()
                        );
                        if (!row) return;

                        row.invoiceRequestContainerId = item.invoiceRequestContainerId ?? null;
                        row.invoiceRequestInstructionId =
                            item.invoiceRequestInstructionId ?? null;
                        row.needExtensionFor = String(item.pickupLocation ?? '0');
                        row.plannedPickupDate = safeDate(item.pickupDate);
                        row.currency = item.currency || 'XOF';
                        row.deliveryZone = String(item.deliveryZoneId ?? row.deliveryZone ?? '');
                        row.deliveryZoneName = item.deliveryZoneName ?? row.deliveryZoneName ?? '';
                        row.subLocation = String(item.subLocationId ?? row.subLocation ?? '');
                        row.subLocationName = item.subLocationName ?? row.subLocationName ?? '';
                        row.modeOfTransport = String(item.modeOfTransport ?? row.modeOfTransport ?? '');
                        row.typeOfTrailer = String(item.typeOfTrailer ?? row.typeOfTrailer ?? '');
                        row.isHazardous = item.isHazardous ?? row.isHazardous ?? false;
                    });
                } catch {
                    containerTableData = [];
                }
            }

            return {
                next: null,
                previous: null,
                data: {
                    invoiceRequestId: data?.invoiceRequestInstructionId ?? null,
                    blId: data?.blId ?? null,
                    blNo: data?.blNo ?? '',
                    countryCode: data?.countryCode ?? '',
                    linerId: data?.linerId ?? null,
                    requestedPartnerId: data?.requestedPartnerId ?? null,
                    typeOfDocument: data?.typeOfDocument ?? '',
                    customsClearanceChannel: data?.customsClearanceChannel ?? '',
                    partyToInvoice: data?.partyToInvoice ?? '',
                    methodOfPayment: data?.methodOfPayment ?? '',
                    deliveryZoneId: data?.deliveryZoneId ?? '',
                    deliveryZone: data?.deliveryZone ?? '',
                    subLocationId: data?.subLocationId ?? '',
                    subLocationName: data?.subLocationName ?? '',
                    isHazardous: data?.isHazardous ?? false,
                    modeOfTransport: data?.modeOfTransport ?? '',
                    typeOfTrailer: data?.typeOfTrailer ?? '',
                    invoiceType: data?.invoiceType ?? '',
                    emailAddress: data?.emailAddress ?? '',
                    contactNumber: data?.contactNumber ?? '',
                    remarks: data?.remarks ?? '',
                    deliveryLocation: data?.deliveryLocation ?? '',
                    approverRemarks: data?.approverRemarks ?? '',
                    linerRemarks: data?.linerRemarks ?? '',
                    status: data?.status ?? null,
                    invoiceRequestInstructionAtts: data?.invoiceRequestInstructionAtts ?? [],
                    invoiceTypeAccessDtlsList: data?.invoiceTypeAccessDtlsList ?? [],
                    customerDocuments: documents,
                    attList,
                    updatedDocListData: updatedDocList,
                    dropdownDocList,
                    containerList: savedContainerList,
                    containerTableData,
                },
            };
        } catch (error: any) {
            return rejectWithValue(
                error?.response?.data || { responseMsg: 'Something went wrong' }
            );
        }
    }
);

export const getInvoiceRequestCustomerAttConf = createAsyncThunk(
    'invoiceRequest/getInvoiceRequestCustomerAttConf',
    async (payload: { country: string; liner: any }) => {
        try {
            const response = await axiosInstance.get(
                `/api/edo-rest-v2/v2/common/attachments?countryCode=${payload.country}&linerId=${payload.liner}&instructionType=822`
            );

            return response.data;
        } catch {
            return [];
        }
    }
);

export const approveRejectInvoiceRequest = createAsyncThunk(
    
    'invoiceRequest/approveRejectInvoiceRequest',
    async (payload: any, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(
                `/api/edo-rest-v2/v2/invoiceRequest/approveInvoiceRequestInstruction`,
                payload
            );

            const data = response.data ?? {};
            if (data.status === 'FAILED') {
                return rejectWithValue(data);
            }
            if (data.status === 'FAILED' || data.responseCode === 'FAILED') {
                return rejectWithValue(data);
            }
            //console.log("response",response);
            return data;
        } catch (error: any) {
            const errData = error?.response?.data;
            return rejectWithValue(
                errData?.status === 'FAILED' || errData?.responseMsg
                    ? errData
                    : {
                          status: 'FAILED',
                          responseMsg: 'Error while processing Invoice Request',
                      }
            );
        }
    }
);

export const getInvoiceRequestTypeOfDocumentList = createAsyncThunk(
    'invoiceRequest/getInvoiceRequestTypeOfDocumentList',
    async ({ dataCountry, linerId }: any) => {
        try {
            const response = await axiosCacheInstance.get(
                `/api/edo-rest-v2/v2/mstCode/getMstCodeListByTypeAndLanguage?language=EN&codeType=TYPE_OF_DOCUMENT&linerId=${linerId}&countryCode=${dataCountry}`,
                { cache: { ttl: 10 * 60 * 1000 } }
            );

            return (
                response.data?.map((item: any) => ({
                    value: item.codeId,
                    label: item.codeDesc,
                })) ?? []
            );
        } catch {
            return [];
        }
    }
);

export const getInvoiceRequestCustomsClearanceChannelList = createAsyncThunk(
    'invoiceRequest/getInvoiceRequestCustomsClearanceChannelList',
    async ({ dataCountry, linerId }: any) => {
        try {
            const response = await axiosCacheInstance.get(
                `/api/edo-rest-v2/v2/mstCode/getMstCodeListByTypeAndLanguage?language=EN&codeType=CUSTOMS_CLEARANCE_CHANNEL&linerId=${linerId}&countryCode=${dataCountry}`,
                { cache: { ttl: 10 * 60 * 1000 } }
            );

            return (
                response.data?.map((item: any) => ({
                    value: item.codeId,
                    label: item.codeDesc,
                })) ?? []
            );
        } catch {
            return [];
        }
    }
);

export const getInvoiceRequestPaymentModeList = createAsyncThunk(
    'invoiceRequest/getInvoiceRequestPaymentModeList',
    async ({ dataCountry, linerId }: any) => {
        try {
            const response = await axiosCacheInstance.get(
                `/api/edo-rest-v2/v2/mstCode/getMstCodeListByTypeAndLanguage?language=EN&codeType=METHOD_PAYMENT_LIST&linerId=${linerId}&countryCode=${dataCountry}`,
                { cache: { ttl: 10 * 60 * 1000 } }
            );

            return (
                response.data?.map((item: any) => ({
                    value: item.codeId,
                    label: item.codeDesc,
                })) ?? []
            );
        } catch {
            return [];
        }
    }
);

export const getInvoiceRequestDeliveryZoneList = createAsyncThunk(
    'invoiceRequest/getInvoiceRequestDeliveryZoneList',
    async ({ countryCode, linerId }: { countryCode: string; linerId: any }) => {
        try {
            const response = await axiosCacheInstance.get(
                `/api/edo-rest-v2/v2/invoiceRequest/getAllDeliveryZones?linerId=${linerId}&countryCode=${countryCode}`,
                { cache: { ttl: 10 * 60 * 1000 } }
            );

            return (
                response.data?.map((item: any) => ({
                    value: item.deliveryZoneId,
                    label: item.deliveryZoneName,
                })) ?? []
            );
        } catch {
            return [];
        }
    }
);

export const getInvoiceRequestSubLocationList = createAsyncThunk(
    'invoiceRequest/getInvoiceRequestSubLocationList',
    async ({ linerId, deliveryZoneId, deliveryZone, countryCode }: { linerId: any; deliveryZoneId: string | number; deliveryZone: string; countryCode: string }) => {
        try {
            const response = await axiosCacheInstance.get(
                `/api/edo-rest-v2/v2/invoiceRequest/getSubLocations?deliveryZone=${encodeURIComponent(
                    deliveryZone
                )}&deliveryZoneId=${deliveryZoneId}&linerId=${linerId}&countryCode=${countryCode}`,
                { cache: { ttl: 10 * 60 * 1000 } }
            );

            return (
                response.data?.map((item: any) => ({
                    value: item.subLocationId,
                    label: item.subLocationName,
                })) ?? []
            );
        } catch {
            return [];
        }
    }
);

export const getInvoiceRequestZoneBasedTransportList = createAsyncThunk(
    'invoiceRequest/getInvoiceRequestZoneBasedTransportList',
    async ({ dataCountry, linerId }: any) => {
        try {
            const response = await axiosCacheInstance.get(
                `/api/edo-rest-v2/v2/mstCode/getMstCodeListByTypeAndLanguage?language=EN&codeType=ZONE_BASED_TRANSPORT&linerId=${linerId}&countryCode=${dataCountry}`,
                { cache: { ttl: 10 * 60 * 1000 } }
            );

            return (
                response.data?.map((item: any) => ({
                    value: item.codeId,
                    label: item.codeDesc,
                })) ?? []
            );
        } catch {
            return [];
        }
    }
);

export const getInvoiceRequestModeOfTransportList = createAsyncThunk(
    'invoiceRequest/getInvoiceRequestModeOfTransportList',
    async ({ dataCountry, linerId }: any) => {
        try {
            const response = await axiosCacheInstance.get(
                `/api/edo-rest-v2/v2/mstCode/getMstCodeListByTypeAndLanguage?language=EN&codeType=MODE_OF_TRANSPORT&linerId=${linerId}&countryCode=${dataCountry}`,
                { cache: { ttl: 10 * 60 * 1000 } }
            );

            return (
                response.data?.map((item: any) => ({
                    value: item.codeId,
                    label: item.codeDesc,
                })) ?? []
            );
        } catch {
            return [];
        }
    }
);

export const getInvoiceRequestInvChangeTypeList = createAsyncThunk(
    'invoiceRequest/getInvoiceRequestInvChangeTypeList',
    async ({ dataCountry, linerId }: any) => {
        try {
            let codeType = 'INV_REQ_TYPE';
            let response = await axiosCacheInstance.get(
                `/api/edo-rest-v2/v2/mstCode/getMstCodeListByTypeAndLanguage?language=EN&codeType=${codeType}&linerId=${linerId}&countryCode=${dataCountry}`,
                { cache: { ttl: 10 * 60 * 1000 } }
            );

            if (
                response?.data == null ||
                (Array.isArray(response.data) && response.data.length === 0)
            ) {
                codeType = 'INV_TYPE';
                response = await axiosCacheInstance.get(
                    `/api/edo-rest-v2/v2/mstCode/getMstCodeListByTypeAndLanguage?language=EN&codeType=${codeType}&linerId=${linerId}&countryCode=${dataCountry}`,
                    { cache: { ttl: 10 * 60 * 1000 } }
                );
            }

            const list = Array.isArray(response?.data)
                ? response.data
                : response?.data?.data ?? [];

            return (
                list.map((item: any) => ({
                    value: item.codeId,
                    label: item.codeDesc,
                })) ?? []
            );
        } catch {
            return [];
        }
    }
);

// export const getInvoiceRequestCountryAreaCodeList = createAsyncThunk(
//     'invoiceRequest/getInvoiceRequestCountryAreaCodeList',
//     async ({ dataCountry, linerId }: any) => {
//         try {
//             const response = await axiosCacheInstance.get(
//                 `/api/edo-rest-v2/v2/mstCode/getMstCodeListByTypeAndLanguage?language=EN&codeType=INVOICE_CONTACT_CODE&linerId=${linerId}&countryCode=${dataCountry}`,
//                 { cache: { ttl: 10 * 60 * 1000 } }
//             );
//
//             return (
//                 response.data?.map((item: any) => ({
//                     value: item.codeId,
//                     label: item.codeDesc,
//                 })) ?? []
//             );
//         } catch {
//             return [];
//         }
//     }
// );

export const getInvoiceRequestDeliveryLocationList = createAsyncThunk(
    'invoiceRequest/getInvoiceRequestDeliveryLocationList',
    async ({ dataCountry, linerId }: any) => {
        try {
            const response = await axiosCacheInstance.get(
                `/api/edo-rest-v2/v2/mstCode/getMstCodeListByTypeAndLanguage?language=EN&codeType=DELIVERY_LOCATION&linerId=${linerId}&countryCode=${dataCountry}`,
                { cache: { ttl: 10 * 60 * 1000 } }
            );

            return (
                response.data?.map((item: any) => ({
                    value: item.codeId,
                    label: item.codeDesc,
                })) ?? []
            );
        } catch {
            return [];
        }
    }
);

export const getTypeofTrailerList = createAsyncThunk(
    'invoiceRequest/getTypeofTrailerList',
    async ({ dataCountry, linerId }: any) => {
        try {
            const response = await axiosCacheInstance.get(
                `/api/edo-rest-v2/v2/mstCode/getMstCodeListByTypeAndLanguage?language=EN&codeType=TYPE_OF_TRAILER&linerId=${linerId}&countryCode=${dataCountry}`,
                { cache: { ttl: 10 * 60 * 1000 } }
            );

            return (
                response.data?.map((item: any) => ({
                    value: item.codeId,
                    label: item.codeDesc,
                })) ?? []
            );
        } catch {
            return [];
        }
    }
);

export const validateBlNumber = createAsyncThunk(
    'invoiceRequest/validateBlNumber',
    async (blNo: string, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(
                `/api/edo-rest-v2/v2/invoiceRequest/checkBlExist`,
                {
                    params: { blNo },
                }
            );

            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error?.response?.data || { responseMsg: 'Something went wrong' }
            );
        }
    }
);
